import { reaction } from "mobx";
import { useRef, useCallback, useEffect } from "react";

import { usePlayground } from "../../playground-store";

const SCROLL_DELAY_MS = 100;

const useMessageScrolling = () => {
  const playground = usePlayground();
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = useCallback(() => {
    if (playground.isChat) {
      messagesEndRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "end",
      });
    }
  }, [playground.isChat]);

  const handleScrollOnChange = useCallback(
    (newValue: number, prevValue: number) => {
      if (newValue > prevValue && playground.isChat) {
        setTimeout(scrollToBottom, SCROLL_DELAY_MS);
      }
    },
    [scrollToBottom, playground.isChat],
  );

  const handleMessageContentChange = useCallback(
    (newContent: string, oldContent: string) => {
      if (
        newContent.length > oldContent.length &&
        playground.isPendingNewMessage &&
        playground.isChat
      ) {
        setTimeout(scrollToBottom, SCROLL_DELAY_MS);
      }
    },
    [playground, scrollToBottom],
  );

  useEffect(() => {
    const messageReaction = reaction(() => playground.messages.length, handleScrollOnChange, {
      fireImmediately: false,
      delay: SCROLL_DELAY_MS,
    });

    const messageContentReaction = reaction(
      () => playground.messages.map(message => message.content),
      (newContents, oldContents) => {
        newContents.forEach((content, index) => {
          if (oldContents[index] !== undefined) {
            handleMessageContentChange(content, oldContents[index]);
          }
        });
      },
      { fireImmediately: false, delay: SCROLL_DELAY_MS },
    );

    return () => {
      messageReaction();
      messageContentReaction();
    };
  }, [playground.messages, playground, handleScrollOnChange, handleMessageContentChange]);

  return { messagesEndRef };
};

export default useMessageScrolling;
